import { AXIS_KEYS, AxisKey, IdentifierAxisUsage } from '../../ProductIdentifierTSX/types';

/**
 * Whether an edit to a saved product's Size Range, Size, Colourway, Variable 1 or Variable 2 would
 * make the next save delete Product Identifier rows (BM-9211).
 *
 * Saving Product Information reconciles the identifier rows against the product's selections. A
 * row survives only when, on every axis, its value is still selected — or it has no value there
 * and the axis is still unselected. Everything else is deleted along with the values typed into
 * it. The page asks before letting that happen.
 */

/** The product's selections as plain id lists, one per combination axis. */
export type IdentifierSelections = Record<AxisKey, string[]>;

/** The product fields each combination axis is read from. */
export const IDENTIFIER_AXIS_FIELDS = ['colourLibraries', 'sizeRangeId', 'sizeRangeSizes', 'legLengths'];

/**
 * Normalises one id as the form or the API may hold it: a string, an ObjectId-like value, or a
 * populated `{ _id }` / dropdown `{ value }` object.
 * @param item The raw entry.
 * @returns The id as a string, or an empty string when there is none.
 */
const toId = (item: unknown): string => {
  if (item === null || item === undefined || item === '') return '';
  if (typeof item === 'object') {
    const record = item as { _id?: unknown; value?: unknown };
    if (record._id !== undefined && record._id !== null) return String(record._id);
    if (record.value !== undefined && record.value !== null) return String(record.value);
  }
  return String(item);
};

/**
 * Normalises a raw id list, dropping blanks and duplicates.
 * @param value The raw field off the product.
 * @returns Distinct string ids, in source order.
 */
const toIdList = (value: unknown): string[] => {
  const list = Array.isArray(value) ? value : value === null || value === undefined || value === '' ? [] : [value];
  return Array.from(new Set(list.map(toId).filter(Boolean)));
};

/**
 * Reads the product's current selection on each combination axis.
 *
 * Variable 2 contributes the chosen option ids, not the variable's own id — the same values the
 * reconcile builds rows from. A legacy single-id `optionId` is tolerated.
 * @param product The product as held in the form, or as loaded from the API.
 * @returns One id list per axis.
 */
export const selectionsFromProduct = (product: Record<string, any> | null | undefined): IdentifierSelections => ({
  colourwayId: toIdList(product?.colourLibraries),
  sizeId: toIdList(product?.sizeRangeSizes),
  lengthId: toIdList(product?.legLengths),
  additionalVariableId: toIdList(product?.additionalVariables?.optionId),
});

/**
 * Decides whether saving these selections removes a value the saved identifier rows use.
 *
 * Only a removal or a replacement warns: an axis that drops an id some row uses. Adding values never
 * warns — including a first value on an axis the saved rows leave empty, which the save answers by
 * rebuilding those rows with the new value rather than by the user taking anything away. Given the
 * selections from before the edit, only ids the user actually had selected count, so a saved row
 * pointing at a value the product no longer holds cannot make an addition warn.
 * @param usage The saved rows' per-axis summary; absent means nothing is known to be at risk.
 * @param selections The selections the edit would save.
 * @param previous The selections before the edit; when omitted every id the rows use counts.
 * @returns True when a value some saved row uses is no longer selected.
 */
export const wouldDeleteIdentifierRows = (
  usage: IdentifierAxisUsage | null | undefined,
  selections: IdentifierSelections,
  previous?: IdentifierSelections,
): boolean => {
  if (!usage || usage.rowCount === 0) return false;
  return AXIS_KEYS.some((key) => {
    const axis = usage.axes?.[key];
    if (!axis) return false;
    const selected = new Set(selections[key]);
    const wasSelected = previous ? new Set(previous[key]) : null;
    return axis.ids.some((id) => !selected.has(String(id)) && (!wasSelected || wasSelected.has(String(id))));
  });
};

/**
 * Builds the usage the saved product implies, for when the real one could not be read.
 *
 * The reconcile lays down the full combination of the saved selections, so treating that as the
 * row set errs on the side of warning: any removal of a saved value still raises the popup. Used
 * only as a fallback, never in place of the server's answer.
 * @param product The product as it was loaded, before any edit.
 * @returns A usage equivalent to the saved selections' combination rows.
 */
export const usageFromSavedProduct = (product: Record<string, any> | null | undefined): IdentifierAxisUsage => {
  const selections = selectionsFromProduct(product);
  const hasRows = AXIS_KEYS.some((key) => selections[key].length > 0);
  const axes = {} as IdentifierAxisUsage['axes'];
  AXIS_KEYS.forEach((key) => {
    axes[key] = { ids: selections[key], hasEmpty: hasRows && selections[key].length === 0 };
  });
  return { rowCount: hasRows ? 1 : 0, axes };
};

/**
 * An axis change held back until the user accepts that it deletes identifier rows: either a plain
 * field change routed through `handleSelect`, or a Variable 2 option change.
 */
export type PendingIdentifierChange =
  | { kind: 'select'; key: string; value: unknown; name?: unknown }
  | { kind: 'variable'; variableId: string; optionIds: string[] };
